import { useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  PhoneIncoming, Clock, Users, Voicemail, PhoneOff,
  MessageSquare, Menu, PhoneForwarded, Music, Volume2,
  ChevronDown, ChevronRight, Edit2, X, Eye,
  ArrowDown, GitBranch,
} from 'lucide-react';

// ── Step metadata ──
const STEP_META = {
  incoming:  { label: 'Incoming Call', icon: PhoneIncoming, bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', iconBg: 'bg-blue-100', dot: 'bg-blue-400' },
  greeting:  { label: 'Greeting', icon: MessageSquare, bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', iconBg: 'bg-teal-100', dot: 'bg-teal-400' },
  call_hours:{ label: 'Call Hours', icon: Clock, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', iconBg: 'bg-amber-100', dot: 'bg-amber-400' },
  menu:      { label: 'IVR Menu', icon: Menu, bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', iconBg: 'bg-indigo-100', dot: 'bg-indigo-400' },
  simul_ring:{ label: 'Simul-Ring', icon: Users, bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', iconBg: 'bg-green-100', dot: 'bg-green-400' },
  forward:   { label: 'Forward Call', icon: PhoneForwarded, bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', iconBg: 'bg-cyan-100', dot: 'bg-cyan-400' },
  voicemail: { label: 'Voicemail', icon: Voicemail, bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', iconBg: 'bg-purple-100', dot: 'bg-purple-400' },
  end_call:  { label: 'End Call', icon: PhoneOff, bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', iconBg: 'bg-red-100', dot: 'bg-red-400' },
};

function getMeta(type) { return STEP_META[type] || STEP_META.end_call; }

function trimStr(s, n) { return s && s.length > n ? `"${s.slice(0, n)}..."` : s ? `"${s}"` : null; }

function getSummary(step) {
  const c = step.config || {};
  switch (step.type) {
    case 'greeting': return c.audio_file_id ? null : trimStr(c.message, 55);
    case 'call_hours': return c.mode === '24_7' ? '24/7 — Always open' : c.mode === 'business_hours' ? 'Company business hours' : 'Custom hours';
    case 'menu': return trimStr(c.prompt_text, 50);
    case 'simul_ring': return `${c.target === 'ring_group' ? 'Ring group' : c.target === 'employee' ? 'Employee' : 'All web phones'} · ${c.timeout || 25}s`;
    case 'forward': return c.number ? `${c.number} · ${c.timeout || 30}s` : 'No number set';
    case 'voicemail': return c.audio_file_id ? null : trimStr(c.message, 55);
    case 'end_call': return 'Hang up';
    default: return null;
  }
}

function getPromptInfo(step) {
  if (!['greeting', 'menu', 'voicemail'].includes(step.type)) return null;
  const c = step.config || {};
  if (c.audio_file_id) return { label: 'Audio', Icon: Music, cls: 'bg-violet-100 text-violet-700' };
  if (c.message || c.prompt_text) return { label: 'TTS', Icon: Volume2, cls: 'bg-slate-100 text-slate-600' };
  return null;
}

function getBranchLabel(key, parentType) {
  if (parentType === 'call_hours') return key === 'open' ? 'Open Hours' : 'After Hours';
  if (key === 'timeout') return 'No Input / Timeout';
  if (key === 'invalid') return 'Invalid Input';
  if (/^[0-9*#]$/.test(key)) return `Press ${key}`;
  return key;
}

function getBranchColor(key, parentType) {
  if (parentType === 'call_hours') return key === 'open' ? 'border-green-300 bg-green-50 text-green-700' : 'border-amber-300 bg-amber-50 text-amber-700';
  if (key === 'timeout') return 'border-amber-300 bg-amber-50 text-amber-700';
  if (key === 'invalid') return 'border-red-300 bg-red-50 text-red-700';
  return 'border-indigo-300 bg-indigo-50 text-indigo-700';
}

function orderBranchKeys(keys, parentType) {
  if (parentType === 'menu') {
    const order = '1234567890*#';
    const digits = keys.filter(k => /^[0-9*#]$/.test(k)).sort((a, b) => order.indexOf(a) - order.indexOf(b));
    const tail = [];
    if (keys.includes('timeout')) tail.push('timeout');
    if (keys.includes('invalid')) tail.push('invalid');
    return [...digits, ...tail];
  }
  if (parentType === 'call_hours') {
    const out = [];
    if (keys.includes('open')) out.push('open');
    if (keys.includes('closed')) out.push('closed');
    return out;
  }
  return keys;
}

function countAllSteps(steps) {
  let n = 0;
  for (const s of (steps || [])) {
    n++;
    if (s.branches) for (const k of Object.keys(s.branches)) n += countAllSteps(s.branches[k]);
  }
  return n;
}


// ══════════════════════════════════════════════════════════════
// Desktop tree — uses plain render functions (no recursive components)
// ══════════════════════════════════════════════════════════════

function renderNode(step, key) {
  const m = getMeta(step.type);
  const Icon = m.icon;
  const summary = getSummary(step);
  const pi = getPromptInfo(step);
  const isTerminal = step.type === 'end_call' || step.type === 'voicemail';
  const branches = step.branches;
  const bKeys = branches ? orderBranchKeys(Object.keys(branches), step.type) : [];

  return (
    <div key={key} className="flex flex-col items-center" data-testid={`preview-node-${step.type}`}>
      <div className={`rounded-lg border px-3 py-2 min-w-[140px] max-w-[220px] ${m.bg} ${m.border} ${isTerminal ? 'ring-1 ring-offset-1' : ''}`}>
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${m.iconBg} ${m.text}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span className={`text-xs font-semibold ${m.text}`}>{m.label}</span>
        </div>
        {(summary || pi) && (
          <div className="mt-1.5 pl-8 space-y-1">
            {pi && <span className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded font-medium ${pi.cls}`}><pi.Icon className="w-2.5 h-2.5" /> {pi.label}</span>}
            {summary && <p className="text-[10px] text-muted-foreground leading-tight break-words">{summary}</p>}
          </div>
        )}
      </div>

      {bKeys.length > 0 && (
        <div className="mt-1">
          <div className="flex justify-center py-0.5"><GitBranch className="w-3.5 h-3.5 text-muted-foreground/50" /></div>
          <div className="flex gap-3 justify-center flex-wrap">
            {bKeys.map(bk => (
              <div key={bk} className="flex flex-col items-center" data-testid={`preview-branch-${bk}`}>
                <div className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border mb-1 ${getBranchColor(bk, step.type)}`}>
                  {getBranchLabel(bk, step.type)}
                </div>
                {renderStepList(branches[bk] || [])}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function renderStepList(steps) {
  if (!steps || steps.length === 0) {
    return <div className="rounded border border-dashed border-muted-foreground/30 px-3 py-2 text-[10px] text-muted-foreground italic">No actions</div>;
  }
  return (
    <div className="flex flex-col items-center">
      {steps.map((step, idx) => (
        <div key={idx} className="flex flex-col items-center">
          {idx > 0 && <div className="flex justify-center py-0.5"><ArrowDown className="w-3.5 h-3.5 text-muted-foreground/50" /></div>}
          {renderNode(step, idx)}
        </div>
      ))}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Mobile tree — flatten into a list of items with depth, then
// render with a single non-recursive component
// ══════════════════════════════════════════════════════════════

function flattenForMobile(steps, depth, parentType, pathPrefix = '') {
  const items = [];
  for (let i = 0; i < (steps || []).length; i++) {
    const step = steps[i];
    const itemId = pathPrefix ? `${pathPrefix}-${i}` : `${depth}-${i}`;
    items.push({ kind: 'step', step, depth, id: itemId });
    if (step.branches) {
      const bKeys = orderBranchKeys(Object.keys(step.branches), step.type);
      for (const bk of bKeys) {
        const branchId = `${itemId}-b-${bk}`;
        items.push({ kind: 'branch-label', key: bk, parentType: step.type, depth: depth + 1, id: branchId });
        const bSteps = step.branches[bk] || [];
        if (bSteps.length === 0) {
          items.push({ kind: 'empty', depth: depth + 2, id: `${branchId}-empty` });
        } else {
          items.push(...flattenForMobile(bSteps, depth + 2, step.type, branchId));
        }
      }
    }
  }
  return items;
}

function MobileTree({ steps }) {
  const [collapsed, setCollapsed] = useState({});
  const flatItems = flattenForMobile(steps, 0, null);

  const toggle = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  // Track which branch-labels are collapsed so we skip their children
  const visibleItems = [];
  const skipUntilDepth = [];

  for (const item of flatItems) {
    // Skip if a parent branch is collapsed
    if (skipUntilDepth.length > 0) {
      const threshold = skipUntilDepth[skipUntilDepth.length - 1];
      if (item.depth >= threshold) continue;
      skipUntilDepth.pop(); // Done skipping
    }

    visibleItems.push(item);

    if (item.kind === 'branch-label' && collapsed[item.id]) {
      skipUntilDepth.push(item.depth + 1);
    }
  }

  return (
    <div className="space-y-1" data-testid="preview-mobile-tree">
      {visibleItems.map(item => {
        const pl = `${item.depth * 16}px`;

        if (item.kind === 'branch-label') {
          const clr = getBranchColor(item.key, item.parentType);
          const lbl = getBranchLabel(item.key, item.parentType);
          const isCollapsed = collapsed[item.id];
          return (
            <div key={item.id} style={{ paddingLeft: pl }}>
              <button
                onClick={() => toggle(item.id)}
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${clr}`}
                data-testid={`preview-mobile-branch-${item.key}`}
              >
                {isCollapsed ? <ChevronRight className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                {lbl}
              </button>
            </div>
          );
        }

        if (item.kind === 'empty') {
          return (
            <div key={item.id} style={{ paddingLeft: pl }}>
              <p className="text-[10px] text-muted-foreground italic">No actions</p>
            </div>
          );
        }

        // kind === 'step'
        const step = item.step;
        const m = getMeta(step.type);
        const Icon = m.icon;
        const summary = getSummary(step);
        const pi = getPromptInfo(step);

        return (
          <div key={item.id} style={{ paddingLeft: pl }} data-testid={`preview-mobile-node-${step.type}`}>
            <div className={`rounded-lg border px-3 py-2 ${m.bg} ${m.border}`}>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${m.iconBg} ${m.text}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className={`text-xs font-semibold ${m.text}`}>{m.label}</span>
              </div>
              {(summary || pi) && (
                <div className="mt-1 pl-8 space-y-0.5">
                  {pi && <span className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded font-medium ${pi.cls}`}><pi.Icon className="w-2.5 h-2.5" /> {pi.label}</span>}
                  {summary && <p className="text-[10px] text-muted-foreground leading-tight">{summary}</p>}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// FlowPreviewPanel — exported main content
// ══════════════════════════════════════════════════════════════

export function FlowPreviewPanel({ flow, onClose, onEdit }) {
  const steps = flow?.steps_json || [];
  const totalSteps = countAllSteps(steps);
  const hasMenu = steps.some(s => s.type === 'menu');
  const hasBranches = steps.some(s => s.branches && Object.keys(s.branches).length > 0);

  return (
    <div className="space-y-4" data-testid="flow-preview-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">{flow?.name || 'Call Flow'}</h3>
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <Badge variant="outline" className="text-[10px]">{totalSteps} steps</Badge>
            {hasMenu && <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">IVR</Badge>}
            {hasBranches && <Badge className="bg-teal-100 text-teal-700 text-[10px]">Branching</Badge>}
            {flow?.is_default && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Default</Badge>}
            <Badge variant="outline" className="text-[10px]">{flow?.assigned_number_ids?.length || 0} numbers</Badge>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onEdit && (
            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={onEdit} data-testid="preview-edit-btn">
              <Edit2 className="w-3 h-3" /> Edit Flow
            </Button>
          )}
          {onClose && (
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onClose} data-testid="preview-close-btn">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground border-b pb-2" data-testid="preview-legend">
        {Object.entries(STEP_META).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${v.dot}`} />{v.label}</span>
        ))}
        <span className="flex items-center gap-1"><Music className="w-2.5 h-2.5 text-violet-500" /> Audio</span>
        <span className="flex items-center gap-1"><Volume2 className="w-2.5 h-2.5 text-slate-400" /> TTS</span>
      </div>

      {steps.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-8">This flow has no steps configured.</p>
      ) : (
        <>
          <div className="hidden sm:flex justify-center overflow-x-auto py-4" data-testid="preview-desktop-tree">
            {renderStepList(steps)}
          </div>
          <div className="sm:hidden">
            <MobileTree steps={steps} />
          </div>
        </>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// FlowPreviewModal — overlay wrapper
// ══════════════════════════════════════════════════════════════

export function FlowPreviewModal({ flow, onClose, onEdit }) {
  if (!flow) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center" data-testid="flow-preview-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-background rounded-xl shadow-2xl border w-full max-w-3xl mx-4 mt-8 sm:mt-16 max-h-[85vh] overflow-y-auto p-4 sm:p-6">
        <FlowPreviewPanel flow={flow} onClose={onClose} onEdit={onEdit} />
      </div>
    </div>
  );
}
